import { PanelTitle } from '../reporting/PanelTitle';
import { cn } from '../../lib/utils';

// The metric card for the lead-bot report.
//
// ⚠️ THE SAME TREATMENT AS THE OTHER DASHBOARDS — `PanelTitle`, the same border,
// the same figure weight. This is a sibling of the reporting pages, not a new
// visual language, and the shared title component is what stops the two drifting
// (see PanelTitle for why a panel's label is not secondary text).
//
// ⚠️ IT TAKES A STRING, NOT A NUMBER. Every "unmeasured is a dash, never a zero"
// decision has already been made in `reportFormat` by the time a value reaches
// here — a card that took a number would need its own `?? 0` and would be the
// one place the rule could be quietly undone.

export function ReportStatCard({
  label,
  value,
  caption,
  tone = 'neutral',
}: {
  label: string;
  /** Already formatted — see reportFormat. */
  value: string;
  /** What the figure is over. ⚠️ On a rate this carries the denominator. */
  caption?: string;
  /**
   * `alert` for a Tier-1 figure that is only interesting when it is non-zero —
   * a compliance trigger, a send failure. ⚠️ It tints only when there is
   * something to see, so a quiet week does not render as a wall of red.
   */
  tone?: 'neutral' | 'alert';
}) {
  const alerting = tone === 'alert' && value !== '0' && value !== '—';
  return (
    <div
      data-testid={`stat-${label}`}
      className={cn(
        'rounded-lg border bg-white p-4',
        alerting ? 'border-amber-300 bg-amber-50/40' : 'border-slate-200',
      )}
    >
      <PanelTitle title={label}>{label}</PanelTitle>
      <div
        className={cn(
          'mt-1 text-2xl font-semibold leading-tight tabular-nums',
          alerting ? 'text-amber-800' : 'text-slate-900',
        )}
      >
        {value}
      </div>
      {caption && <p className="mt-1 text-xs text-muted-foreground">{caption}</p>}
    </div>
  );
}
